import { useState, useEffect } from "react";
import axios from "axios";
import { 
  History, 
  Clock, 
  ArrowRight,
  Pencil,
  Trash2,
  Plus,
  RotateCcw,
  X
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  ScrollArea
} from "@/components/ui/scroll-area";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANGE_TYPE_CONFIG = {
  update: { icon: Pencil, color: "bg-blue-100 text-blue-700", label: "Updated" },
  add: { icon: Plus, color: "bg-green-100 text-green-700", label: "Added" },
  delete: { icon: Trash2, color: "bg-red-100 text-red-700", label: "Deleted" },
  reset: { icon: RotateCcw, color: "bg-amber-100 text-amber-700", label: "Reset" }
};

function formatFieldPath(fieldPath) {
  if (!fieldPath || fieldPath === "*") return "All Fields";
  
  return fieldPath
    .split(".")
    .map(part => part.charAt(0).toUpperCase() + part.slice(1).replace(/_/g, " "))
    .join(" → ");
}

function formatValue(value) {
  if (value === null || value === undefined) return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "object") {
    if (Array.isArray(value)) {
      return value.length > 0 ? `[${value.length} items]` : "Empty list";
    }
    return JSON.stringify(value).substring(0, 100) + (JSON.stringify(value).length > 100 ? "..." : "");
  }
  const str = String(value);
  return str.length > 80 ? str.substring(0, 80) + "..." : str;
}

function formatTimestamp(timestamp) {
  if (!timestamp) return "Unknown";
  const date = new Date(timestamp);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
}

function ChangeEntry({ change }) {
  const config = CHANGE_TYPE_CONFIG[change.change_type] || CHANGE_TYPE_CONFIG.update;
  const Icon = config.icon;
  
  return (
    <div className="border-b border-zinc-100 last:border-0 py-3 px-1">
      <div className="flex items-start gap-3">
        <div className={`p-1.5 rounded ${config.color}`}>
          <Icon className="w-3.5 h-3.5" />
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="font-medium text-sm text-zinc-800">
              {formatFieldPath(change.field_path)}
            </span>
            <Badge className={`text-[10px] ${config.color}`}>
              {config.label}
            </Badge>
          </div>
          
          {change.change_type !== "reset" && change.change_type !== "delete" && (
            <div className="flex items-start gap-2 text-xs mb-1.5">
              <div className="flex-1 min-w-0">
                <span className="text-zinc-400">From:</span>
                <span className="ml-1 text-zinc-600">{formatValue(change.old_value)}</span>
              </div>
              <ArrowRight className="w-3 h-3 text-zinc-400 mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <span className="text-zinc-400">To:</span>
                <span className="ml-1 text-zinc-800 font-medium">{formatValue(change.new_value)}</span>
              </div>
            </div>
          )}
          
          {change.notes && (
            <p className="text-xs text-zinc-500 italic mt-1">"{change.notes}"</p>
          )}
          
          <div className="flex items-center gap-1 text-[10px] text-zinc-400 mt-1.5">
            <Clock className="w-3 h-3" />
            {formatTimestamp(change.timestamp)}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function ChangelogViewer({ 
  countryCode, 
  countryName,
  countryFlag,
  isOpen, 
  onClose 
}) {
  const [changes, setChanges] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchChangelog = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/country-customizations/${countryCode}/changelog?limit=100`);
      setChanges(res.data?.changes || []);
    } catch (err) {
      console.error("Error fetching changelog:", err);
      setError("Failed to load edit history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && countryCode) {
      fetchChangelog();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, countryCode]);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[550px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5 text-violet-600" />
            Edit History
            {countryFlag && <span className="text-xl">{countryFlag}</span>}
            {countryName && <span className="font-normal text-zinc-600">— {countryName}</span>}
          </DialogTitle>
        </DialogHeader>
        
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <div className="loading-spinner" />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-sm text-red-500">{error}</p>
            <Button size="sm" variant="outline" onClick={fetchChangelog} className="mt-2">
              Retry
            </Button>
          </div>
        ) : changes.length === 0 ? (
          <div className="text-center py-8">
            <History className="w-12 h-12 text-zinc-300 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-zinc-700 mb-1">No Edit History</h3>
            <p className="text-xs text-zinc-500">
              No customizations have been made to {countryName || countryCode} yet.
            </p>
          </div>
        ) : (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-3">
              <span className="text-xs text-zinc-500">{changes.length} changes recorded</span>
              <Badge variant="outline" className="text-xs">
                {countryCode}
              </Badge>
            </div>
            
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-0">
                {changes.map((change, idx) => (
                  <ChangeEntry key={change.id || idx} change={change} />
                ))}
              </div>
            </ScrollArea>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Compact button component for triggering the changelog viewer
export function ViewHistoryButton({ countryCode, countryName, countryFlag, hasCustomizations }) {
  const [isOpen, setIsOpen] = useState(false);
  
  if (!hasCustomizations) return null;
  
  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        className="h-6 px-2 text-xs text-zinc-500 hover:text-violet-600"
        onClick={() => setIsOpen(true)}
        data-testid={`view-history-${countryCode}`}
      >
        <History className="w-3 h-3 mr-1" />
        History
      </Button>
      
      <ChangelogViewer
        countryCode={countryCode}
        countryName={countryName}
        countryFlag={countryFlag}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
}
